import { useState, useEffect } from 'react';
import { Tabs } from '../components/common/Tabs';
import PageHeader from '../components/common/PageHeader';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useShellNavigate } from '../components/common/ShellLink';
import { useAuth } from '../contexts/AuthContext';
import { useEntitlements } from '../contexts/EntitlementContext';
import { appliesToLabel } from './settingsScope';
import { safeReturnPath } from '../components/common/returnPath';
import api from '../services/api';
import AIHub from './AIHub';
import { PROVIDER_PARAM } from './LLMSettings';
import { PARSER_PARAM } from './SiteParsers';
import { useDrillLevel } from '../components/common/drillLevel';
import ConnectionsHub from './ConnectionsHub';
import PageGenerationHub, { PageGenerationSub } from './PageGenerationHub';
import NetworkHub from './NetworkHub';
import UserApprovals from './UserApprovals';
import AdminBilling from './AdminBilling';
import AdminMargin from './AdminMargin';
import { isLegacyTargetsLink } from './settingsLinks';
import BrandSettings from './BrandSettings';
import KeywordScoringSettings from './KeywordScoringSettings';
import type { Product } from '../types';
import AdminPill from '../components/common/AdminPill';

// ============ Main Settings Component ============
// Account is deliberately NOT here — it's your own profile, not app config, so
// it lives at /account behind the username in the top-right. `?tab=account`
// redirects there.
type SettingsTab = 'ai' | 'page-generation' | 'keywords' | 'brand' | 'connections' | 'network' | 'users' | 'billing' | 'margin';

const VALID_TABS: SettingsTab[] = [
  'ai',
  'network',
  'connections',
  'page-generation',
  'keywords',
  'brand',
  'users',
  'billing',
  'margin',
];

// Legacy tab keys → the hub sub-view they now live in (for old bookmarks / in-app links).
const CONNECTION_SUBS = ['github', 'google-ads', 'image-storage', 'amazon'];
const PAGE_GENERATION_LEGACY: Record<string, PageGenerationSub> = {
  'page-specs': 'specs',
  'page-builder-research': 'research',
};

// Declaration order IS the rendered tab order. AI leads because it's the most-used tab,
// and it's also the default landing (see resolveTab).
//
// PRODUCT SCOPING IS WHY THIS EXISTS AS DATA. Settings is where most of the
// cross-product clutter lived: a Brainstorms customer opened it and found GitHub
// deploy keys, Google Ads credentials, Amazon affiliate config and page-spec
// editors — none of which mean anything to them, on the screen where you go to
// make the thing yours. Tagging the tabs and filtering shrinks that to three.
//
// A tab with no `product` is genuinely shared: every product burns LLM tokens
// (AI), brainstorm research crawls just like SERP fetching does (Network), and
// the reader renders all three artifact kinds in the sender's brand (Brand).
const TAB_CONFIG: {
  key: SettingsTab;
  label: string;
  adminOnly?: boolean;
  product?: Product;
  /** Shown when you hold any of these: a tab two products share. */
  anyOf?: Product[];
}[] = [
  // Providers, Models, Model policy (which models may do each job), and LLM Usage live under
  // this tab (AIHub). A customer gets ONLY Providers — their own keys, which
  // the house mapping runs on. Which model runs which stage, and what it
  // cost, is plumbing and stays admin-only inside the hub — see
  // contexts/InternalsContext for the rest of the same argument.
  { key: 'ai', label: 'AI' },
  // Network settings, Site Parsers, and Network Usage all live under this one tab (NetworkHub).
  { key: 'network', label: 'Network' },
  // GitHub, Google Ads, Image Storage, Amazon Affiliate and Email spy live under this tab
  // (ConnectionsHub). Email spy is recon's, so a recon-only account sees the tab too; the hub
  // shows each one only the connections its products use.
  { key: 'connections', label: 'Connections', anyOf: ['PUBLISH', 'BOARDROOM'] },
  // Page specs and Builder research live under this tab (PageGenerationHub) —
  // how pages get WRITTEN. Where they SHIP is Deliver, not a setting.
  { key: 'page-generation', label: 'Page Generation', product: 'PUBLISH' },
  // When a keyword gets the ★ and a page-one result counts as open: the
  // keyword lists (Publish) and recon's "Where to win" (Boardroom) both read it.
  { key: 'keywords', label: 'Keywords', anyOf: ['PUBLISH', 'BOARDROOM'] },
  // What a READER sees on a shared artifact. Its own tab rather than a sub-view of
  // Page Generation because it applies to every artifact kind — briefings and
  // brainstorm reports are not pages.
  { key: 'brand', label: 'Brand' },
  // Approving/rejecting signups — admin-only, was a top-level nav item.
  { key: 'users', label: 'Users', adminOnly: true },
  // The price book (credits per action) and the house models free users run
  // on. Admin-only: what a customer sees is their own balance, on /billing.
  { key: 'billing', label: 'Billing', adminOnly: true },
  // Billed vs cost vs margin, live. The one screen that shows dollars we pay.
  { key: 'margin', label: 'Margin', adminOnly: true },
];

/** Where a request we cannot honour lands. AI is admin-only now, so the safe
 *  landing for everyone else is Brand — it carries no `product`, which means it
 *  is visible in every shell. */
function fallbackTab(isAdmin: boolean): SettingsTab {
  return isAdmin ? 'ai' : 'brand';
}

function resolveTab(
  tab: string | null,
  isAdmin: boolean,
  held: Product[] = [],
): SettingsTab {
  // Legacy tab keys now fold into a hub's sub-view.
  if (tab === 'models' || tab === 'pipeline' || tab === 'usage')
    return fallbackTab(isAdmin);
  if (tab === 'network-usage') return 'network';
  if (tab && CONNECTION_SUBS.includes(tab)) return 'connections';
  if (tab && tab in PAGE_GENERATION_LEGACY) return 'page-generation';
  // A non-admin can't reach Users, even by URL.
  if ((tab === 'users' || tab === 'billing' || tab === 'margin') && !isAdmin) return fallbackTab(isAdmin);
  // Nor can a deep link reach a tab this shell does not show. Same failure as
  // the admin case: the tab would resolve, fail the `visibleTabs` filter, and
  // render a header over nothing — which reads as a broken screen rather than a
  // closed door.
  const scoped = TAB_CONFIG.find((t) => t.key === tab);
  if (scoped?.product && !held.includes(scoped.product)) return fallbackTab(isAdmin);
  if (scoped?.anyOf && !scoped.anyOf.some((p) => held.includes(p))) return fallbackTab(isAdmin);
  // An admin-only tab reached by URL falls back the same way a product tab does.
  if (scoped?.adminOnly && !isAdmin) return fallbackTab(isAdmin);
  if (tab && VALID_TABS.includes(tab as SettingsTab)) return tab as SettingsTab;
  return fallbackTab(isAdmin); // default landing (was 'models')
}


export default function Settings() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const shellNavigate = useShellNavigate();
  const { user } = useAuth();
  // Levels opened inside a tab (a provider's editor, a parser's drawer). The
  // header's back button closes whichever is open before it leaves Settings.
  const providerLevel = useDrillLevel(PROVIDER_PARAM);
  const parserLevel = useDrillLevel(PARSER_PARAM);

  const isAdmin = !!user?.is_admin;
  const { held } = useEntitlements();

  // Top-level tab state — seeded from URL
  const [activeTab, setActiveTab] = useState<SettingsTab>(() =>
    resolveTab(searchParams.get('tab'), isAdmin, held)
  );

  // Count of applicants awaiting review, badged on the Users tab. User Approvals
  // used to be a top-level nav item carrying this badge; keep the signal now that
  // it's tucked into Settings. Refetch on tab change so it clears after acting.
  const [pendingApprovals, setPendingApprovals] = useState(0);
  useEffect(() => {
    if (!isAdmin) return;
    api.getPendingUserCount()
      .then(setPendingApprovals)
      .catch(() => {});
  }, [isAdmin, activeTab]);

  // Sync state FROM URL when searchParams change externally (e.g. link click)
  useEffect(() => {
    const urlTab = resolveTab(searchParams.get('tab'), isAdmin, held);
    setActiveTab(urlTab);
    // `held` is intentionally a dependency: entitlements arrive after the first
    // render, so a deep-linked product tab has to be re-resolved once they do.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, isAdmin, held.join(',')]);

  // Normalize legacy bookmarks to the canonical hub locations once (old standalone tabs are
  // now hub sub-views). `?tab=pipeline&stage=X` keeps its stage; `?tab=network-usage`→usage.
  const rawTab = searchParams.get('tab');
  const legacyNetworkUsage = rawTab === 'network-usage';
  const legacyPipeline = rawTab === 'pipeline';
  const legacyLlmUsage = rawTab === 'usage';
  // An old `?tab=github|google-ads|image-storage|amazon` deep-link → the Connections sub.
  const legacyConnectionSub =
    rawTab && CONNECTION_SUBS.includes(rawTab) ? rawTab : null;
  // An old `?tab=page-specs|page-builder-research` deep-link → the Page Generation sub.
  const legacyPageGenerationSub = rawTab ? PAGE_GENERATION_LEGACY[rawTab] ?? null : null;

  // Account left Settings for its own page. A query param can't be matched by a
  // route, so the old deep-link is bounced from here.
  useEffect(() => {
    if (rawTab === 'account') navigate('/account', { replace: true });
  }, [rawTab, navigate]);

  // Old Deployment Targets links go to Deliver. See isLegacyTargetsLink.
  const rawSub = searchParams.get('sub');
  const isLegacyTargets = isLegacyTargetsLink(
    rawTab,
    rawSub,
    searchParams.get('site'),
    searchParams.get('plan'),
  );
  useEffect(() => {
    if (!isLegacyTargets) return;
    const p = new URLSearchParams();
    for (const key of ['site', 'plan']) {
      const v = searchParams.get(key);
      if (v) p.set(key, v);
    }
    const qs = p.toString();
    // Settings renders in every shell; /deliver only exists in Publish. A plain
    // navigate here left an old bookmark on brainstorm.lumnian.com/deliver.
    shellNavigate(`/deliver${qs ? `?${qs}` : ''}`);
  }, [isLegacyTargets, searchParams, shellNavigate]);

  useEffect(() => {
    let target: { tab: string; sub?: string } | null = null;
    if (rawTab === 'network-usage') target = { tab: 'network', sub: 'usage' };
    else if (rawTab === 'pipeline') target = { tab: 'ai', sub: 'policy' };
    else if (rawTab === 'ai' && searchParams.get('sub') === 'pipeline')
      target = { tab: 'ai', sub: 'policy' }; // the old name of the model policy sub
    else if (rawTab === 'usage') target = { tab: 'ai', sub: 'usage' };
    else if (rawTab === 'models') target = { tab: 'ai', sub: 'models' };
    else if (legacyConnectionSub)
      target = { tab: 'connections', sub: legacyConnectionSub };
    else if (legacyPageGenerationSub)
      target = { tab: 'page-generation', sub: legacyPageGenerationSub };
    if (!target) return;
    const p = new URLSearchParams(searchParams);
    p.set('tab', target.tab);
    if (target.sub) p.set('sub', target.sub);
    else p.delete('sub');
    setSearchParams(p, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Where the user was before a deep-link (a model chip, an error link) sent
  // them here, so we can offer a way back. Only in-app paths are honored — a
  // `from` pointing off-site, or protocol-relative ("//evil"), is ignored.
  // The guard lives in components/common/returnPath now — the page builder's
  // "Feeding this build" links land in three more screens, and three more
  // hand-rolled copies of an open-redirect check is three chances to get it
  // wrong.
  const returnPath = safeReturnPath(searchParams.get('from'));
  // `returnLabel` was assigned here and never rendered: the trail was CARRIED
  // across tab changes (see below) but never shown, so a deep-link into Settings
  // preserved a way back that nobody could see. ReturnTrail reads both params
  // itself, so the fix is to render it, not to thread these through.
  const returnLabel = searchParams.get('from_label');
  void returnLabel;

  // Handle tab switching — update URL, which triggers the effect above.
  // Everything else is dropped (a `stage` deep-link shouldn't survive a tab
  // change) EXCEPT the return trail: looking around settings before heading
  // back is the normal case, not a reason to strand them.
  const handleTabChange = (tab: SettingsTab) => {
    const params = new URLSearchParams();
    if (tab !== 'ai') {
      params.set('tab', tab);
    }
    if (returnPath) {
      params.set('from', returnPath);
      if (returnLabel) params.set('from_label', returnLabel);
    }
    setSearchParams(params, { replace: true });
  };

  // ENTITLEMENT GATES; THE SHELL DOES NOT.
  //
  // This briefly used the nav's shell-first rule, so Publish's Connections tab
  // vanished inside Brainstorms. Wrong, and instructively so: there is ONE
  // settings store. The AI model picked in Brainstorms is the same row Publish
  // reads, so hiding the tab implied a separation the data does not have — and
  // it created the worse confusion, since "where did that tab go?" is harder to
  // recover from than "why is this here?". The second question answers itself
  // the moment you read the label (see `appliesToLabel`).
  //
  // What the shell DOES change is nothing at all here. What entitlement changes
  // is everything: a customer without Publish never sees Connections.
  const appliesLabel = appliesToLabel(activeTab, held);

  const openLevel =
    activeTab === 'ai' && providerLevel.value !== null
      ? providerLevel
      : activeTab === 'network' && parserLevel.value !== null
      ? parserLevel
      : null;

  const visibleTabs = TAB_CONFIG.filter(
    (t) =>
      (!t.adminOnly || user?.is_admin) &&
      (!t.product || held.includes(t.product)) &&
      (!t.anyOf || t.anyOf.some((p) => held.includes(p))),
  );

  // Page Generation hosts the Deployment Targets tables, which were a full-width
  // page before the move — give that tab the extra width back.
  const containerWidth = activeTab === 'page-generation' ? 'max-w-7xl' : 'max-w-6xl';

  return (
    <div className={`${containerWidth} mx-auto`}>
      {/* THE WORD "SETTINGS" NEVER APPEARED ON THE SETTINGS PAGE. It went
          straight from the app nav into a tab bar, so the only heading anywhere
          on the route was an <h1> of dummy copy inside the Brand tab's preview
          mock ("Offshore wind, week 34") — which is what a screen reader read
          out as the page title.

          The header also owns the return trail now. Settings is usually a
          destination, but a deep-link can drop you here mid-task (a model chip
          clicked from a page build), and browser-back stops working the moment
          you switch a tab. */}
      {/* The exit comes from PageHeader's ReturnTrail. The trail was already
          being CARRIED across tab changes (see the tab handler below) and simply
          never rendered, so a deep-link into Settings preserved a way back that
          nobody could see — and the header's own Settings link never had one at
          all. `returnFallback` covers that second case, which is most visits. */}
      {/* An open editor or drawer is a level inside Settings: back closes it
          first, and only from the list does it go wherever you came from. */}
      <PageHeader
        title="Settings"
        subtitle="How this account works - models, connections, how pages get written, and what a reader sees."
        returnFallback={{ path: '/', label: 'home' }}
        returnLevel={
          openLevel
            ? { path: openLevel.parentPath, label: 'settings', onBack: openLevel.close }
            : null
        }
      />

      {/* Top-level Settings Tabs */}
      <Tabs
        className="mb-6"
        active={activeTab}
        onChange={handleTabChange}
        tabs={visibleTabs.map((tab) => ({
          key: tab.key,
          label: tab.label,
          badge: (
            <>
              {tab.key === 'users' && pendingApprovals > 0 && (
                <span className="inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 rounded-full text-xs font-semibold bg-red-500 text-white">
                  {pendingApprovals}
                </span>
              )}
              {tab.adminOnly && <AdminPill />}
            </>
          ),
        }))}
      />

      {/* WHICH OF YOUR APPS THIS PAGE CHANGES.
          One line, above the settings themselves, because the honest fact about
          this screen is that there is ONE settings store behind three apps: the
          model picked here is the same row every product reads. Hiding a tab per
          shell implied a separation that does not exist; saying what a page
          affects is the truth, and it answers "why am I seeing this" without
          anyone having to ask.

          Silent for a one-product account and for Users — see appliesToLabel. */}
      {appliesLabel && (
        <p className="-mt-3 mb-5 text-xs text-gray-400 dark:text-gray-500">
          {appliesLabel}
        </p>
      )}

      {/* Tab Content */}
      {activeTab === 'ai' && (
        <AIHub
          isAdmin={isAdmin}
          initialSub={
            legacyPipeline ? 'policy' : legacyLlmUsage ? 'usage' : 'providers'
          }
        />
      )}
      {activeTab === 'page-generation' && (
        <PageGenerationHub initialSub={legacyPageGenerationSub || 'specs'} />
      )}
      {activeTab === 'connections' && (
        <ConnectionsHub initialSub={(legacyConnectionSub as any) || 'github'} />
      )}
      {activeTab === 'network' && (
        <NetworkHub initialSub={legacyNetworkUsage ? 'usage' : 'settings'} />
      )}
      {activeTab === 'keywords' && <KeywordScoringSettings />}
      {activeTab === 'brand' && <BrandSettings />}
      {activeTab === 'users' && isAdmin && <UserApprovals />}
      {activeTab === 'billing' && isAdmin && <AdminBilling />}
      {activeTab === 'margin' && isAdmin && <AdminMargin />}
    </div>
  );
}
